import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Folder, 
  File, 
  Home, 
  ArrowLeft, 
  ArrowRight,
  HardDrive,
  Monitor,
  FileText,
  Settings,
  Image,
  Music,
  Grid3X3,
  List,
  LayoutGrid,
  Table,
  FolderIcon,
  ChevronUp,
  ChevronDown,
  Presentation,
  Sheet,
  Play
} from 'lucide-react';
import { RefreshCw } from 'lucide-react';
import { Upload, Download } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FileSystemNavigator, FileSystemItem } from '../../utils/filesystem';
import { addRefreshCallback, removeRefreshCallback } from '../WindowContent';
import { ContextMenu, useContextMenu, ContextMenuItem } from '../ui/context-menu-custom';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../hooks/useSettings';
import { Toggle } from '../ui/toggle';
import { makeProgramIcon } from '../AppIcon';

const ExplorerFileIcon = makeProgramIcon('file', 'document');
const TextFileIcon = makeProgramIcon('text-file', 'document');
const BatchFileIcon = makeProgramIcon('batch-file', 'document');
const SystemFileIcon = makeProgramIcon('system-file', 'settings');
const ImageFileIcon = makeProgramIcon('image-file', 'image');
const Mp3FileIcon = makeProgramIcon('mp3-file', 'music');
const WavFileIcon = makeProgramIcon('wav-file', 'music');
const MusicFileIcon = makeProgramIcon('music-file', 'music');
const MapleSeedFileIcon = makeProgramIcon('maple-seed', 'archive');
const ExplorerFolderIcon = makeProgramIcon('folder', 'folder');
const MyComputerIcon = makeProgramIcon('my-computer', 'monitor');
const SidebarDesktopIcon = makeProgramIcon('desktop', 'monitor');
const DesktopFolderIcon = makeProgramIcon('desktop-folder', 'folder');
const MyDocumentsIcon = makeProgramIcon('documents-folder', 'folder');
const PicturesIcon = makeProgramIcon('pictures-folder', 'folder');
const GamesFolderIcon = makeProgramIcon('games-folder', 'folder');
const ProgramFilesFolderIcon = makeProgramIcon('program-files-folder', 'folder');
const LocalDiskIcon = makeProgramIcon('local-disk', 'hard-drive');
const SystemInformationIcon = makeProgramIcon('system-information', 'info');

interface FileExplorerProps {

  onOpenFile?: (filePath: string, content: string) => void;
  // Open a specific app window (preferred when we want to bypass extension routing)
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  navigator?: FileSystemNavigator;
  initialPath?: string;
}

type ViewMode = 'large-icons' | 'medium-icons' | 'small-icons' | 'list' | 'details';

export const FileExplorer: React.FC<FileExplorerProps> = ({ onOpenFile, onOpenWindow, navigator: externalNavigator, initialPath }) => {
  const [navigator] = useState(() => externalNavigator || new FileSystemNavigator());
  const [currentPath, setCurrentPath] = useState(initialPath || 'My Computer');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [currentItems, setCurrentItems] = useState<FileSystemItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('details');
  const [addressBarValue, setAddressBarValue] = useState(initialPath || 'My Computer');
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showMyComputer, setShowMyComputer] = useState(!initialPath || initialPath === 'My Computer');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'modified'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [draggingPath, setDraggingPath] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const recycleBinPath = 'C:\\$Recycle.Bin';
  const isRecycleBinView = !showMyComputer && currentPath === recycleBinPath;

  // NEW: helpers to normalize size/date for reliable sorting
  const normalizeSize = useCallback((item: FileSystemItem) => {
    const s: any = item;
    if (typeof s.size === 'number') return s.size;
    if (typeof s.sizeRaw === 'number') return s.sizeRaw;
    if (typeof s.size === 'string') {
      const m = s.size.match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }
    return 0;
  }, []);

  const parseModified = useCallback((item: FileSystemItem) => {
    // Prefer raw timestamp when provided
    const anyItem = item as any;
    if (typeof anyItem.modifiedMs === 'number') {
      return anyItem.modifiedMs;
    }
    const d: any = item.modified;
    if (!d) return 0;
    const t = Date.parse(String(d));
    if (!Number.isNaN(t)) return t;
    // Fallback for simple date formats like 12/08/2024
    const m = String(d).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const month = parseInt(m[1], 10);
      const day = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      return new Date(year, month - 1, day).getTime();
    }
    return 0;
  }, []);

  // NEW: sorting function (folders first)
  const sortItems = useCallback((items: FileSystemItem[]) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const aIsFolder = a.type === 'folder';
      const bIsFolder = b.type === 'folder';
      if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1; // folders first

      let cmp = 0;
      if (sortBy === 'name') {
        cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      } else if (sortBy === 'size') {
        cmp = normalizeSize(a) - normalizeSize(b);
      } else {
        // modified
        cmp = parseModified(a) - parseModified(b);
      }
      return cmp * dir;
    });
  }, [sortBy, sortDirection, normalizeSize, parseModified]);

  // NEW: change sort when clicking headers
  const handleSort = useCallback((column: 'name' | 'size' | 'modified') => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  }, [sortBy]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]); // Multiple selection
  const [clipboard, setClipboard] = useState<{ items: FileSystemItem[], operation: 'copy' | 'cut' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const addressInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const fileListRef = useRef<HTMLDivElement>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const joinPath = useCallback((basePath: string, name: string) => {
    return basePath.endsWith('\\') ? `${basePath}${name}` : `${basePath}\\${name}`;
  }, []);

  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu();
  const { user, profile } = useAuth();
  const { settings, updateSetting } = useSettings('file-explorer', { showThumbnails: true });
  const showThumbnails = Boolean(settings?.showThumbnails);

  // Hidden file input for Upload
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Helpers to read files
  const readFileAsText = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsText(file);
    });

  const readFileAsDataURL = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Upload handler
  const handleUploadFiles = async (files: FileList | null) => {
    if (!files || showMyComputer) return;
    await navigator.waitForInitialization();

    const toArray = Array.from(files);
    for (const f of toArray) {
      const ext = f.name.split('.').pop()?.toLowerCase();
      const isText =
        (f.type && f.type.startsWith('text/')) ||
        ['txt', 'csv', 'json', 'md', 'js', 'ts', 'tsx', 'css', 'html'].includes(ext || '');

      const content = isText ? await readFileAsText(f) : await readFileAsDataURL(f);
      const targetPath = joinPath(currentPath, f.name);
      await navigator.createFile(targetPath, content);
    }

    // Reset input so same file can be re-uploaded
    if (uploadInputRef.current) uploadInputRef.current.value = '';

    // Refresh view and notify
    refreshItems();
    window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: currentPath } }));
  };

  const triggerUpload = () => {
    if (showMyComputer) return; // not applicable in "My Computer" view
    uploadInputRef.current?.click();
  };

  // Download helper for files
  const downloadItem = async (item: FileSystemItem) => {
    if (item.type !== 'file') return;
    const content = item.content || '';
    let href = '';
    let revokeUrl: string | null = null;

    try {
      if (content.startsWith('data:')) {
        // Data URL can be used directly
        href = content;
      } else if (/^https?:\/\//i.test(content)) {
        // Remote URL -> fetch and download as blob
        const res = await fetch(content);
        const blob = await res.blob();
        href = URL.createObjectURL(blob);
        revokeUrl = href;
      } else {
        // Plain text or unknown -> download as text
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        href = URL.createObjectURL(blob);
        revokeUrl = href;
      }

      const a = document.createElement('a');
      a.href = href;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (revokeUrl) {
        setTimeout(() => URL.revokeObjectURL(revokeUrl!), 1000);
      }
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  // Get user-specific paths
  const getUserPath = (folder: string) => {
    if (!user) return `C:\\${folder}`;
    // Use profile username if available, fallback to email-based username
    const username = profile?.username || user.email?.split('@')[0] || 'User';
    const sanitizedUsername = username.replace(/[^a-zA-Z0-9]/g, '');
    return `C:\\User\\${sanitizedUsername}\\${folder}`;
  };

  const getDesktopPath = () => getUserPath('Desktop');

  const getFolderIcon = (item: FileSystemItem, size: number = 16) => {
    const name = item.name.trim().toLowerCase();
    const path = item.path.trim().toLowerCase();

    if (path === getDesktopPath().toLowerCase() || name === 'desktop') {
      return <DesktopFolderIcon size={size} />;
    }

    if (path === getUserPath('Documents').toLowerCase() || name === 'documents') {
      return <MyDocumentsIcon size={size} />;
    }

    if (path === getUserPath('Pictures').toLowerCase() || name === 'pictures') {
      return <PicturesIcon size={size} />;
    }

    if (name === 'games') {
      return <GamesFolderIcon size={size} />;
    }

    if (name === 'program files') {
      return <ProgramFilesFolderIcon size={size} />;
    }

    return <ExplorerFolderIcon size={size} />;
  };

  // Helper: detect descendant moves (disallow)
  const isDescendantPath = (parent: string, child: string) => {
    const normParent = parent.replace(/\\+$/, '');
    const normChild = child.replace(/\\+$/, '');
    return normChild.startsWith(normParent + '\\');
  };

  // Move source item into a folder (rename path)
  const moveItemToFolder = async (sourcePath: string, targetFolderPath: string) => {
    await navigator.waitForInitialization();
    if (!sourcePath || !targetFolderPath) return false;
    if (sourcePath === targetFolderPath) return false;
    if (isDescendantPath(sourcePath, targetFolderPath)) return false;

    const name = sourcePath.split('\\').pop() || 'item';
    let destPath = `${targetFolderPath}\\${name}`;

    // If exists, create a unique name similar to Windows
    if (navigator.pathExists(destPath)) {
      let counter = 2;
      const base = name.replace(/\.[^.\s]+$/, '');
      const extMatch = name.match(/(\.[^.\s]+)$/);
      const ext = extMatch ? extMatch[1] : '';
      destPath = `${targetFolderPath}\\${base} - Copy${ext}`;
      while (navigator.pathExists(destPath)) {
        destPath = `${targetFolderPath}\\${base} (${counter})${ext}`;
        counter++;
      }
    }

    const ok = await navigator.renameItem(sourcePath, destPath);
    if (ok) {
      // Refresh current view and notify filesystem watchers
      refreshItems();
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: targetFolderPath } }));
    }
    return ok;
  };

  // DnD handlers
  const onItemDragStart = (e: React.DragEvent, item: FileSystemItem) => {
    // Provide the path to other drop targets (e.g., Desktop)
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/maple-path', item.path);
    e.dataTransfer.setData('text/plain', item.path);
    setDraggingPath(item.path);
  };

  const onItemDragEnd = () => {
    setDraggingPath(null);
    setDragOverTarget(null);
  };

  const onFolderDragOver = (e: React.DragEvent, folderPath: string) => {
    if (!draggingPath) return;
    e.preventDefault();
    setDragOverTarget(folderPath);
  };

  const onFolderDrop = async (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    if (draggingPath) {
      await moveItemToFolder(draggingPath, folderPath);
    }
    setDragOverTarget(null);
    setDraggingPath(null);
  };

  const onContentAreaDragOver = (e: React.DragEvent) => {
    if (!draggingPath) return;
    e.preventDefault();
    setDragOverTarget(currentPath);
  };

  const onContentAreaDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (draggingPath) {
      await moveItemToFolder(draggingPath, currentPath);
    }
    setDragOverTarget(null);
    setDraggingPath(null);
  };

  const cycleViewMode = () => {
    console.log('Current view mode:', viewMode);
    const modes: ViewMode[] = ['large-icons', 'medium-icons', 'small-icons', 'list', 'details'];
    const currentIndex = modes.indexOf(viewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    const nextMode = modes[nextIndex];
    console.log('Switching to view mode:', nextMode);
    setViewMode(nextMode);
  };

  // Create user directories when user logs in
  useEffect(() => {
    if (user && navigator) {
      const username = profile?.username || user.email?.split('@')[0] || 'User';
      navigator.createUserDirectories(username).then(() => {
        console.log('User directories created for:', username);
        // Refresh to show the new directories
        if (!showMyComputer) {
          refreshItems();
        }
      }).catch(error => {
        console.error('Failed to create user directories:', error);
      });
    }
  }, [user, profile, navigator]);

  // Update refreshItems to apply sorting
  const refreshItems = useCallback(() => {
    console.log('FileExplorer refreshItems called');
    console.log('showMyComputer:', showMyComputer, 'currentPath:', currentPath);
    console.log('navigator instance:', navigator);
    console.log('navigator VFS size:', navigator ? navigator.getVFSSize() : 'undefined');
    
    if (showMyComputer) {
      setCurrentItems([]);
      console.log('FileExplorer in My Computer mode');
      return;
    }
    const items = navigator.getItemsInPath(currentPath);
    const sorted = sortItems(items);
    setCurrentItems(sorted);
    console.log('FileExplorer refreshed, items in', currentPath, ':', sorted.length);
  }, [showMyComputer, currentPath, navigator, sortItems]);

  // NEW: re-sort when sort settings change
  useEffect(() => {
    setCurrentItems(prev => sortItems(prev));
  }, [sortBy, sortDirection, sortItems]);

  useEffect(() => {
    if (!showMyComputer) {
      navigator.navigate(currentPath);
    }
    refreshItems();
  }, [currentPath, navigator, showMyComputer, refreshItems]);

  
  // Set up refresh callback for when files are saved from other windows
  useEffect(() => {
    console.log('Setting up refresh callback for FileExplorer');
    addRefreshCallback(refreshItems);
    return () => {
      console.log('Removing refresh callback for FileExplorer');
      removeRefreshCallback(refreshItems);
    };
  }, []);

  // Listen for filesystem changes from desktop with debouncing
  useEffect(() => {
    let syncTimeout: NodeJS.Timeout;
    
    const handleFileSystemChanged = async (event: CustomEvent) => {
      const { detail } = event;
      console.log('FileExplorer received fileSystemChanged event for path:', detail.path);
      console.log('Current FileExplorer path:', currentPath);
      
      // Clear any pending sync to debounce rapid events
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
      
      // Debounce VFS sync to prevent infinite loops
      syncTimeout = setTimeout(async () => {
        try {
          console.log('Syncing navigator with VFS...');
          await navigator.syncWithVFS();
          
          // Only refresh if we're viewing the affected path or parent path
          if (currentPath === detail.path || detail.path.startsWith(currentPath) || currentPath.startsWith(detail.path)) {
            console.log('Refreshing FileExplorer because path matches current path');
            // Add a small delay to ensure VFS sync is complete
            setTimeout(() => {
              console.log('Executing delayed refresh');
              refreshItems();
            }, 100);
          } else {
            console.log('Not refreshing - path mismatch');
          }
        } catch (error) {
          console.error('Failed to sync with VFS:', error);
        }
      }, 500); // 500ms debounce
    };

    console.log('Setting up fileSystemChanged listener for FileExplorer at path:', currentPath);
    window.addEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);
    return () => {
      console.log('Removing fileSystemChanged listener for FileExplorer at path:', currentPath);
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
      window.removeEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);
    };
  }, [currentPath, refreshItems]); // Remove navigator dependency

  const handleNavigate = (newPath: string) => {
    setShowMyComputer(false);
    setCurrentPath(newPath);
    setAddressBarValue(newPath);
    setSelectedItem(null);
  };

  const handleBack = () => {
    const prevPath = navigator.goBack();
    if (prevPath) {
      setCurrentPath(prevPath);
      setAddressBarValue(prevPath);
      setSelectedItem(null);
    }
  };

  const handleForward = () => {
    const nextPath = navigator.goForward();
    if (nextPath) {
      setCurrentPath(nextPath);
      setAddressBarValue(nextPath);
      setSelectedItem(null);
    }
  };

  // Add: explicit refresh handler to sync VFS and reload items
  const handleRefresh = useCallback(async () => {
    // Ensure we sync with the underlying virtual FS (IndexedDB) first
    if (navigator?.syncWithVFS) {
      await navigator.syncWithVFS();
    }
    // Then refresh whatever path we're currently viewing
    refreshItems();
  }, [navigator, refreshItems]);

  const handleAddressBarClick = () => {
    setIsEditingAddress(true);
    setTimeout(() => {
      addressInputRef.current?.focus();
      addressInputRef.current?.select();
    }, 0);
  };

  const updateSuggestions = (value: string) => {
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    const allPaths = navigator.getAllPaths();
    const filtered = allPaths
      .filter(path => path.toLowerCase().includes(value.toLowerCase()))
      .sort((a, b) => {
        // Prioritize exact start matches
        const aStarts = a.toLowerCase().startsWith(value.toLowerCase());
        const bStarts = b.toLowerCase().startsWith(value.toLowerCase());
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.length - b.length;
      })
      .slice(0, 8); // Limit to 8 suggestions

    setSuggestions(filtered);
    setSelectedSuggestionIndex(-1);
  };

  const handleAddressBarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPath = addressBarValue.trim();
    if (!trimmedPath) return;

    // Check if the path exists
    const item = navigator.findItemByPath(trimmedPath);
    if (item) {
      if (item.type === 'folder') {
        // Navigate to folder
        handleNavigate(trimmedPath);
      } else if (item.type === 'file') {
        // Open file in appropriate program
        if (onOpenFile && item.content) {
          onOpenFile(item.path, item.content);
        }
      }
    } else {
      // Reset to current path if invalid
      setAddressBarValue(currentPath);
    }
    setIsEditingAddress(false);
    setSuggestions([]);
  };

  const handleAddressBarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressBarValue(value);
    updateSuggestions(value);
  };

  const handleAddressBarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setAddressBarValue(currentPath);
      setIsEditingAddress(false);
      setSuggestions([]);
      return;
    }

    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      } else if (e.key === 'Tab') {
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          setAddressBarValue(suggestions[selectedSuggestionIndex]);
          setSuggestions([]);
          setSelectedSuggestionIndex(-1);
        } else if (suggestions.length > 0) {
          setAddressBarValue(suggestions[0]);
          setSuggestions([]);
        }
      } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        setAddressBarValue(suggestions[selectedSuggestionIndex]);
        const selectedPath = suggestions[selectedSuggestionIndex];
        const item = navigator.findItemByPath(selectedPath);
        if (item) {
          if (item.type === 'folder') {
            handleNavigate(selectedPath);
          } else if (item.type === 'file' && onOpenFile && item.content) {
            onOpenFile(item.path, item.content);
          }
        }
        setIsEditingAddress(false);
        setSuggestions([]);
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setAddressBarValue(suggestion);
    const item = navigator.findItemByPath(suggestion);
    if (item) {
      if (item.type === 'folder') {
        handleNavigate(suggestion);
      } else if (item.type === 'file' && onOpenFile && item.content) {
        onOpenFile(item.path, item.content);
      }
    }
    setIsEditingAddress(false);
    setSuggestions([]);
  };

  const handleAddressBarBlur = (e: React.FocusEvent) => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setAddressBarValue(currentPath);
      setIsEditingAddress(false);
      setSuggestions([]);
    }, 150);
  };

  const handleHome = () => {
    if (currentPath === 'C:\\') {
      // From root of C:, go to My Computer
      setShowMyComputer(true);
      setCurrentPath('My Computer');
      setAddressBarValue('My Computer');
      setSelectedItem(null);
    } else {
      // From anywhere else, go to C:\
      handleNavigate('C:\\');
    }
  };

  const handleUp = () => {
    const parentPath = navigator.getParentPath(currentPath);
    if (parentPath !== currentPath) {
      handleNavigate(parentPath);
    }
  };

  const handleDoubleClick = (item: FileSystemItem) => {
    if (item.type === 'folder') {
      handleNavigate(item.path);
    } else if (item.type === 'file' && onOpenFile) {
      // Double-click behavior keeps routing by extension (Terminal for scripts)
      if (item.extension === 'txt') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'mword') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'mslide' || item.extension === 'mapleslide') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'msheet' || item.extension === 'sheet') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'mmoney' || item.extension === 'mmny') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'mseed') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'bat' || item.extension === 'cmd' || item.extension === 'sh') {
        onOpenFile(item.path, item.content || '');

      } else if (item.extension === 'jpg' || item.extension === 'jpeg' || item.extension === 'png' || item.extension === 'gif') {
        onOpenFile(item.path, item.content || '');
      } else if (item.extension === 'mp3' || item.extension === 'wav' || item.extension === 'flac' || item.extension === 'ogg' || item.extension === 'aac' || item.extension === 'm4a') {
        onOpenFile(item.path, item.content || '');
      }
    }
  };

  const getFileIcon = (item: FileSystemItem, size: number = 16) => {
    if (item.type === 'folder') {
      return getFolderIcon(item, size);
    } else {
      switch (item.extension) {
        case 'mword':
          return <FileText size={size} className="text-blue-600" />;
        case 'mslide':
          return <Presentation size={size} className="text-orange-500" />;
        case 'msheet':
          return <Sheet size={size} className="text-green-600" />;
        case 'mseed':
          return <MapleSeedFileIcon size={size} className="text-primary" />;
        case 'txt':
          return <TextFileIcon size={size} className="text-foreground" />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'webp':
        case 'bmp':
        case 'svg':
          return <ImageFileIcon size={size} className="text-accent" />;

        case 'mp3':
          return <Mp3FileIcon size={size} className="text-secondary" />;
        case 'wav':
          return <WavFileIcon size={size} className="text-secondary" />;
        case 'flac':
        case 'ogg':
        case 'aac':
        case 'm4a':
          return <MusicFileIcon size={size} className="text-secondary" />;
        case 'bat':
          return <BatchFileIcon size={size} className="text-secondary-foreground" />;

        case 'sys':
          return <SystemFileIcon size={size} className="text-destructive" />;
        default:
          return <ExplorerFileIcon size={size} className="text-muted-foreground" />;
      }
    }
  };

  const isImageFile = (item: FileSystemItem) => {
    const ext = (item.extension || '').toLowerCase();
    return ['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext);
  };

  const renderIconOrThumbnail = (item: FileSystemItem, size: number) => {
    if (
      showThumbnails &&
      item.type === 'file' &&
      isImageFile(item) &&
      item.content
    ) {
      // Size buckets per view; use object-cover to create a neat thumbnail
      const px = Math.max(16, size);
      return (
        <img
          src={item.content}
          alt={item.name}
          className="rounded-md border border-border object-cover"
          style={{ width: px, height: px }}
          onError={(e) => {
            // Fallback to icon if the image fails to load
            const target = e.currentTarget as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      );
    }
    return getFileIcon(item, size);
  };

  const getViewIcon = (view: ViewMode) => {
    switch (view) {
      case 'large-icons':
        return <LayoutGrid size={16} />;
      case 'medium-icons':
        return <Grid3X3 size={16} />;
      case 'small-icons':
        return <Grid3X3 size={14} />;
      case 'list':
        return <List size={16} />;
      case 'details':
        return <Table size={16} />;
    }
  };

  const getViewLabel = (view: ViewMode) => {
    switch (view) {
      case 'large-icons':
        return 'Large Icons';
      case 'medium-icons':
        return 'Medium Icons';
      case 'small-icons':
        return 'Small Icons';
      case 'list':
        return 'List';
      case 'details':
        return 'Details';
    }
  };

  const handleFileListRightClick = (e: React.MouseEvent) => {
    const contextItems: ContextMenuItem[] = [
      ...(isRecycleBinView
        ? [
            {
              id: 'empty-recycle-bin',
              label: 'Empty Recycle Bin',
              onClick: async () => {
                await handleEmptyRecycleBin();
              },
              disabled: currentItems.length === 0
            },
            { id: 'separator-recycle-bin', label: '', separator: true }
          ] as ContextMenuItem[]
        : []),
      // Upload (only when not in My Computer)
      ...(showMyComputer ? [] : [ {
        id: 'upload',
        label: 'Upload...',
        icon: <Upload size={16} />,
        onClick: () => triggerUpload()
      }] as ContextMenuItem[]),
      // Separator after upload
      ...(showMyComputer ? [] : [{ id: 'separator-upload', label: '', separator: true }] as ContextMenuItem[]),
      {
        id: 'new',
        label: 'New',
        icon: <ExplorerFolderIcon size={16} />,
        submenu: [
          {
            id: 'new-folder',
            label: 'Folder',
            icon: <ExplorerFolderIcon size={16} />,
            onClick: async () => {
              // Wait for filesystem to be initialized
              await navigator.waitForInitialization();
              
              const newFolderName = `New Folder`;
              const newFolderPath = joinPath(currentPath, newFolderName);
              try {
                const success = await navigator.createFolder(newFolderPath);
                if (success) {
                  refreshItems();
                  
                  // Start editing the new folder immediately
                  setEditingItem(newFolderPath);
                  setEditingValue(newFolderName);
                  
                  // Focus the input after a short delay
                  setTimeout(() => {
                    editInputRef.current?.focus();
                    editInputRef.current?.select();
                  }, 100);
                  
                  // Dispatch filesystem change event
                  window.dispatchEvent(new CustomEvent('fileSystemChanged', {
                    detail: { path: currentPath }
                  }));
                }
              } catch (error) {
                console.error('Failed to create folder:', error);
              }
            }
          },
          {
            id: 'new-text',
            label: 'Text Document',
            icon: <FileText size={16} />,
            onClick: async () => {
              // Wait for filesystem to be initialized
              await navigator.waitForInitialization();
              const newFileName = `New Text Document ${Date.now() % 1000}.txt`;
              const newFilePath = joinPath(currentPath, newFileName);
              try {
                const success = await navigator.createFile(newFilePath, '');
                if (success) {
                  refreshItems();
                  // Open the new file in Notepad
                  if (onOpenFile) {
                    onOpenFile(newFilePath, '');
                  }
                }
              } catch (error) {
                console.error('Failed to create file:', error);
              }
            }
          }
        ],
        disabled: isRecycleBinView
      },
      {
        id: 'separator1',
        label: '',
        separator: true
      },
      {
        id: 'refresh',
        label: 'Refresh',
        onClick: () => {
          refreshItems();
        }
      }
    ];

    showContextMenu(e, contextItems);
  };

  const handleFileItemRightClick = (e: React.MouseEvent, item: FileSystemItem) => {
    e.stopPropagation();
    
    const contextItems: ContextMenuItem[] = [];
    
    // Add "Open with" options for files
    if (item.type === 'file') {
      contextItems.push({
        id: 'open-with',
        label: 'Open with',
        icon: <FileText size={16} />,
        submenu: [
          {
            id: 'open-mapleword',
            label: 'MapleWord',
            icon: (
              <img
                src="/images/mapleos/apps/maple-word.png"
                alt="MapleWord"
                style={{ width: 16, height: 16 }}
                className="rounded-sm"
              />
            ),
            onClick: () => {
              if (onOpenWindow) {
                const title = `MapleWord - ${item.name}`;
                onOpenWindow('maple-word', title, item.path, item.content || '');
              } else if (onOpenFile) {
                onOpenFile(item.path, item.content || '');
              }
            }
          },
          {
            id: 'open-notepad',
            label: 'Notepad',
            icon: <FileText size={16} />,
            onClick: () => {
              // Force open in Notepad (bypass extension routing)
              if (onOpenWindow) {
                const title = `Notepad - ${item.name}`;
                onOpenWindow('notepad', title, item.path, item.content || '');
              } else if (onOpenFile) {
                // Fallback: will route by extension (may open Terminal for .bat)
                onOpenFile(item.path, item.content || '');
              }
            }
          },
          {
            id: 'open-hex',
            label: 'Hex Editor',
            icon: <Grid3X3 size={16} />,
            onClick: () => {
              window.dispatchEvent(new CustomEvent('openFileInApp', {
                detail: {
                  appId: 'hex-editor',
                  filePath: item.path,
                  content: item.content
                }
              }));
            }
          }
        ]
      });
      
      contextItems.push({
        id: 'separator-open',
        label: '',
        separator: true
      });
    }
    
    contextItems.push(
      {
        id: 'open',
        label: 'Open',
        onClick: () => {
          handleDoubleClick(item);
        }
      },
      ...(isRecycleBinView && item.originalPath
        ? [
            {
              id: 'restore',
              label: 'Restore',
              onClick: async () => {
                try {
                  const success = await (navigator as any).restoreFromRecycleBin?.(item.path);
                  if (success) {
                    setSelectedItems([]);
                    refreshItems();
                    window.dispatchEvent(new CustomEvent('fileSystemChanged', {
                      detail: { path: currentPath }
                    }));
                  }
                } catch (error) {
                  console.error('Failed to restore item:', error);
                }
              }
            },
            {
              id: 'separator-restore',
              label: '',
              separator: true
            }
          ] as ContextMenuItem[]
        : []),
      // For batch/shell scripts: Run
      ...(item.type === 'file' && ['bat', 'cmd', 'sh'].includes((item.extension || '').toLowerCase())
        ? [ {
            id: 'run-script',
            label: 'Run',
            icon: <Play size={16} />,
            onClick: () => {
              // Open Terminal and auto-run via WindowContent routing
              if (onOpenFile) {
                onOpenFile(item.path, item.content || '');
              }
            }
          }] as ContextMenuItem[]
        : []),
      // Edit in Notepad: explicitly open Notepad regardless of extension
      ...(item.type === 'file'
        ? [ {
            id: 'edit-notepad',
            label: 'Edit',
            icon: <FileText size={16} />,
            onClick: () => {
              // Prefer direct Notepad opening via onOpenWindow
              if (onOpenWindow) {
                const title = `Notepad - ${item.name}`;
                onOpenWindow('notepad', title, item.path, item.content || '');
              } else if (onOpenFile) {
                // Fallback: will route by extension (may open Terminal for .bat)
                onOpenFile(item.path, item.content || '');
              }
            }
          }] as ContextMenuItem[]
        : []),
      // Add Download for files
      ...(item.type === 'file' ? [ {
        id: 'download',
        label: 'Download',
        icon: <Download size={16} />,
        onClick: async () => {
          await downloadItem(item);
        }
      }] as ContextMenuItem[] : []),
      {
        id: 'separator1',
        label: '',
        separator: true
      },
      {
        id: 'rename',
        label: 'Rename',
        onClick: () => {
          setEditingItem(item.path);
          setEditingValue(item.name);
          // Focus the input after a short delay
          setTimeout(() => {
            editInputRef.current?.focus();
            editInputRef.current?.select();
          }, 100);
        },
        disabled: isRecycleBinView
      },
      {
        id: 'delete',
        label: isRecycleBinView ? 'Delete Permanently' : 'Delete',
        onClick: async () => {
          try {
            const success = await navigator.deleteItem(item.path);
            if (success) {
              refreshItems();
              
              // Dispatch filesystem change event
              window.dispatchEvent(new CustomEvent('fileSystemChanged', {
                detail: { path: currentPath }
              }));
            }
          } catch (error) {
            console.error('Failed to delete item:', error);
          }
        }
      },
      {
        id: 'separator2',
        label: '',
        separator: true
      },
      {
        id: 'properties',
        label: 'Properties',
        disabled: true
      }
    );

    showContextMenu(e, contextItems);
  };

  const handleMyComputerRightClick = (e: React.MouseEvent) => {
    const myComputerContextItems: ContextMenuItem[] = [
      {
        id: 'properties',
        label: 'Properties',
        icon: <Settings size={16} />,
        onClick: () => {
          if (onOpenFile) {
            // This will trigger opening the System Properties window
            onOpenFile('system-properties', 'System Properties');
          }
        }
      }
    ];

    showContextMenu(e, myComputerContextItems);
  };

  const handleRenameSubmit = async (item: FileSystemItem) => {
    if (editingValue.trim() === '' || editingValue === item.name) {
      setEditingItem(null);
      setEditingValue('');
      return;
    }

    try {
      const parentPath = navigator.getParentPath(item.path);
      const newPath = joinPath(parentPath, editingValue.trim());

      // Use recursive rename to preserve all contents
      const success = await navigator.renameItem(item.path, newPath);
      if (!success) {
        // Fallback: reset editing state without changes
        setEditingItem(null);
        setEditingValue('');
        return;
      }

      setEditingItem(null);
      setEditingValue('');
      refreshItems();

      window.dispatchEvent(new CustomEvent('fileSystemChanged', {
        detail: { path: newPath }
      }));
    } catch (error) {
      console.error('Failed to rename item:', error);
      setEditingItem(null);
      setEditingValue('');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!fileListRef.current || showMyComputer) return;

      // F2 - Rename
      if (e.key === 'F2' && selectedItems.length === 1) {
        e.preventDefault();
        const item = currentItems.find(item => item.id === selectedItems[0]);
        if (item) {
          setEditingItem(item.path);
          setEditingValue(item.name);
          setTimeout(() => {
            editInputRef.current?.focus();
            editInputRef.current?.select();
          }, 100);
        }
      }

      // Delete - Move to recycle bin
      if (e.key === 'Delete' && selectedItems.length > 0) {
        e.preventDefault();
        handleDeleteSelected();
      }

      // Ctrl+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedItems.length > 0) {
        e.preventDefault();
        handleCopy();
      }

      // Ctrl+X - Cut
      if ((e.ctrlKey || e.metaKey) && e.key === 'x' && selectedItems.length > 0) {
        e.preventDefault();
        handleCut();
      }

      // Ctrl+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        e.preventDefault();
        handlePaste();
      }

      // F5 - Refresh
      if (e.key === 'F5') {
        e.preventDefault();
        handleRefresh();
      }

      // Ctrl+A - Select All
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedItems(currentItems.map(item => item.id));
      }

      // Enter - Open selected item
      if (e.key === 'Enter' && selectedItems.length === 1) {
        e.preventDefault();
        const item = currentItems.find(item => item.id === selectedItems[0]);
        if (item) {
          handleDoubleClick(item);
        }
      }

      // Escape - Clear selection
      if (e.key === 'Escape') {
        setSelectedItems([]);
        setEditingItem(null);
        setEditingValue('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedItems, currentItems, clipboard, showMyComputer, handleRefresh]);

  // Clipboard operations
  const handleCopy = () => {
    const selectedFileItems = currentItems.filter(item => selectedItems.includes(item.id));
    setClipboard({ items: selectedFileItems, operation: 'copy' });
    console.log('Copied', selectedFileItems.length, 'items');
  };

  const handleCut = () => {
    const selectedFileItems = currentItems.filter(item => selectedItems.includes(item.id));
    setClipboard({ items: selectedFileItems, operation: 'cut' });
    console.log('Cut', selectedFileItems.length, 'items');
  };

  const handlePaste = async () => {
    if (!clipboard) return;

    for (const item of clipboard.items) {
      const newName = clipboard.operation === 'cut' ? item.name : `Copy of ${item.name}`;
      const newPath = joinPath(currentPath, newName);
      
      try {
        if (item.type === 'folder') {
          await navigator.createFolder(newPath);
        } else {
          await navigator.createFile(newPath, item.content || '');
        }

        // If cut operation, delete original
        if (clipboard.operation === 'cut') {
          await navigator.deleteItem(item.path);
        }
      } catch (error) {
        console.error('Failed to paste item:', error);
      }
    }

    // Clear clipboard after cut operation
    if (clipboard.operation === 'cut') {
      setClipboard(null);
    }

    refreshItems();
    window.dispatchEvent(new CustomEvent('fileSystemChanged', {
      detail: { path: currentPath }
    }));
  };

  const handleDeleteSelected = async () => {
    for (const itemId of selectedItems) {
      const item = currentItems.find(item => item.id === itemId);
      if (item) {
        try {
          await navigator.deleteItem(item.path);
        } catch (error) {
          console.error('Failed to delete item:', error);
        }
      }
    }
    
    setSelectedItems([]);
    refreshItems();
    window.dispatchEvent(new CustomEvent('fileSystemChanged', {
      detail: { path: currentPath }
    }));
  };

  const handleEmptyRecycleBin = async () => {
    if (!isRecycleBinView || !(navigator as any).emptyRecycleBin) return;

    try {
      await (navigator as any).emptyRecycleBin();
      setSelectedItems([]);
      refreshItems();
      window.dispatchEvent(new CustomEvent('fileSystemChanged', {
        detail: { path: recycleBinPath }
      }));
    } catch (error) {
      console.error('Failed to empty recycle bin:', error);
    }
  };

  const handleDeleteAction = async () => {
    if (selectedItems.length > 0) {
      await handleDeleteSelected();
      return;
    }

    if (isRecycleBinView && currentItems.length > 0) {
      await handleEmptyRecycleBin();
    }
  };

  // Handle item selection with Ctrl/Shift
  const handleItemClick = (itemId: string, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Ctrl+Click: Toggle selection
      setSelectedItems(prev => 
        prev.includes(itemId) 
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId]
      );
    } else if (event.shiftKey && selectedItems.length > 0) {
      // Shift+Click: Select range
      const lastSelectedIndex = currentItems.findIndex(item => item.id === selectedItems[selectedItems.length - 1]);
      const currentIndex = currentItems.findIndex(item => item.id === itemId);
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      const rangeIds = currentItems.slice(start, end + 1).map(item => item.id);
      setSelectedItems(rangeIds);
    } else {
      // Normal click: Single selection
      setSelectedItems([itemId]);
      setSelectedItem(itemId);
    }
  };

  return (
    <div className="h-full flex flex-col" onClick={hideContextMenu} ref={fileListRef}>
      {/* Toolbar */}
      <div className="border-b border-border p-2 bg-muted/50">
        <div className="flex items-center gap-2 mb-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handleBack}
            disabled={!navigator.canGoBack()}
          >
            <ArrowLeft size={16} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handleForward}
            disabled={!navigator.canGoForward()}
          >
            <ArrowRight size={16} />
          </Button>
          <Button variant="ghost" size="sm" className="px-2" onClick={handleUp}>
            <ArrowLeft size={16} className="rotate-90" />
          </Button>
          <Button variant="ghost" size="sm" className="px-2" onClick={handleHome}>
            <Home size={16} />
          </Button>
          
          {/* Separator */}
          <div className="w-px h-6 bg-border mx-1" />
          
          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            onClick={handleRefresh}
            title="Refresh (F5)"
          >
            <RefreshCw size={16} />
          </Button>
          
          {/* Separator */}
          <div className="w-px h-6 bg-border mx-1" />
          
          {/* File Operations */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handleCopy}
            disabled={selectedItems.length === 0}
            title="Copy (Ctrl+C)"
          >
            📋
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handleCut}
            disabled={selectedItems.length === 0}
            title="Cut (Ctrl+X)"
          >
            ✂️
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handlePaste}
            disabled={!clipboard}
            title="Paste (Ctrl+V)"
          >
            📋➡️
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2"
            onClick={handleDeleteAction}
            disabled={isRecycleBinView ? currentItems.length === 0 : selectedItems.length === 0}
            title={isRecycleBinView ? (selectedItems.length > 0 ? 'Delete permanently' : 'Empty Recycle Bin') : 'Delete (Del)'}
          >
            🗑️
          </Button>
          
          {/* View Selector */}
          <div className="ml-auto flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={cycleViewMode}
              title={`Current: ${getViewLabel(viewMode)}. Click to cycle views.`}
            >
              {getViewIcon(viewMode)}
              {getViewLabel(viewMode)}
            </Button>
            
            {/* Thumbnails toggle */}
            <Toggle
              pressed={showThumbnails}
              onPressedChange={(val) => updateSetting('showThumbnails', val)}
              aria-label="Toggle thumbnails"
              title={showThumbnails ? 'Thumbnails: On' : 'Thumbnails: Off'}
              className={cn(
                "h-8 px-2 rounded-lg border transition-colors",
                // base (both themes)
                "bg-transparent !text-inherit hover:bg-muted/60",
                // ON state: soft primary chip with readable text
                "data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:border-primary/30",
                // Dark polish
                "dark:hover:bg-white/10 dark:data-[state=on]:bg-white/10 dark:data-[state=on]:text-white"
              )}
            >
              <Image size={16} className="text-current" />
              <span className="ml-1 hidden sm:inline" />
            </Toggle>
          </div>
        </div>
        
        {/* Address Bar */}
        <div className="flex items-center gap-2 relative">
          {showMyComputer ? <MyComputerIcon size={16} /> : <ExplorerFolderIcon size={16} />}
          <form onSubmit={handleAddressBarSubmit} className="flex-1 relative">
            {isEditingAddress ? (
              <>
                <Input
                  ref={addressInputRef}
                  value={addressBarValue}
                  onChange={handleAddressBarChange}
                  onBlur={handleAddressBarBlur}
                  onKeyDown={handleAddressBarKeyDown}
                  className="h-8 text-sm"
                  placeholder="Enter path (e.g., C:\Documents)"
                />
                {/* Suggestions Dropdown */}
                {suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        className={cn(
                          "px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2",
                          index === selectedSuggestionIndex 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        )}
                        onMouseDown={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      >
                        {(() => {
                          const suggestionItem = navigator.findItemByPath(suggestion);
                          if (suggestionItem?.type === 'folder') {
                            return getFolderIcon(suggestionItem, 14);
                          }
                          return <ExplorerFileIcon size={14} className="text-muted-foreground" />;
                        })()}
                        <span className="truncate">{suggestion}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div
                className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background cursor-text hover:bg-muted/50 transition-colors"
                onClick={handleAddressBarClick}
              >
                {currentPath}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Sidebar and Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Sidebar - Discord Style */}
        <div className="w-14 border-r border-border bg-muted/30 relative flex-shrink-0">
          <div className="p-2 h-full">
            <div className="space-y-1">
              {/* My Computer */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-10 h-10 p-0 flex items-center justify-center",
                    showMyComputer && "bg-primary/20"
                  )}
                  onClick={() => {
                    setShowMyComputer(true);
                    setCurrentPath('My Computer');
                    setAddressBarValue('My Computer');
                    setSelectedItem(null);
                  }}
                  onDragOver={(e) => onFolderDragOver(e, 'C:\\')} // drop to root C:
                  onDrop={(e) => onFolderDrop(e, 'C:\\')}
                >
                  <MyComputerIcon size={20} />
                </Button>
                {/* Flyout label */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                  My Computer
                </div>
              </div>

              {/* Desktop */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-10 h-10 p-0 flex items-center justify-center",
                    currentPath === getDesktopPath() && "bg-primary/20",
                    dragOverTarget === getDesktopPath() && "ring-2 ring-primary"
                  )}
                  onClick={() => handleNavigate(getDesktopPath())}
                  onDragOver={(e) => onFolderDragOver(e, getDesktopPath())}
                  onDrop={(e) => onFolderDrop(e, getDesktopPath())}
                >
                  <SidebarDesktopIcon size={20} />
                </Button>

                {/* Flyout label */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                  Desktop
                </div>
              </div>

              {/* My Documents */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-10 h-10 p-0 flex items-center justify-center",
                    currentPath === getUserPath('Documents') && "bg-primary/20",
                    dragOverTarget === getUserPath('Documents') && "ring-2 ring-primary"
                  )}
                  onClick={() => handleNavigate(getUserPath('Documents'))}
                  onDragOver={(e) => onFolderDragOver(e, getUserPath('Documents'))}
                  onDrop={(e) => onFolderDrop(e, getUserPath('Documents'))}
                >
                  <MyDocumentsIcon size={20} />
                </Button>
                {/* Flyout label */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                  My Documents
                </div>
              </div>

              {/* Pictures */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-10 h-10 p-0 flex items-center justify-center",
                    currentPath === getUserPath('Pictures') && "bg-primary/20",
                    dragOverTarget === getUserPath('Pictures') && "ring-2 ring-primary"
                  )}
                  onClick={() => handleNavigate(getUserPath('Pictures'))}
                  onDragOver={(e) => onFolderDragOver(e, getUserPath('Pictures'))}
                  onDrop={(e) => onFolderDrop(e, getUserPath('Pictures'))}
                >
                  <PicturesIcon size={20} />
                </Button>
                {/* Flyout label */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                  Pictures
                </div>
              </div>

              {/* Local Disk (C:) */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-10 h-10 p-0 flex items-center justify-center",
                    currentPath === 'C:\\' && "bg-primary/20",
                    dragOverTarget === 'C:\\' && "ring-2 ring-primary"
                  )}
                  onClick={() => handleNavigate('C:\\')}
                  onDragOver={(e) => onFolderDragOver(e, 'C:\\')}
                  onDrop={(e) => onFolderDrop(e, 'C:\\')}
                >
                  <LocalDiskIcon size={20} />
                </Button>
                {/* Flyout label */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                  Local Disk (C:)
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File List */}
        <div className="flex-1 flex flex-col min-h-0 md:ml-0">
          <div
           className={cn(
             "flex-1 overflow-auto overscroll-contain p-4",
             dragOverTarget === currentPath && !showMyComputer ? "ring-2 ring-primary/50" : ""
           )}
           onContextMenu={showMyComputer ? handleMyComputerRightClick : handleFileListRightClick}
           onDragOver={!showMyComputer ? onContentAreaDragOver : undefined}
           onDrop={!showMyComputer ? onContentAreaDrop : undefined}
          >
          {/* My Computer View */}
          {showMyComputer ? (
            <div className="space-y-6">
              <div className="text-lg font-semibold mb-4">My Computer</div>
              
              {/* Hard Drives Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Hard Disk Drives</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Local Disk C: */}
                  <div 
                    className="border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onDoubleClick={() => handleNavigate('C:\\')}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <LocalDiskIcon size={32} className="text-blue-600" />
                      <div>
                        <div className="font-medium">Local Disk (C:)</div>
                        <div className="text-xs text-muted-foreground">System Drive</div>
                      </div>
                    </div>
                    
                    {/* Disk Usage */}
                    <div className="space-y-2">
                      {(() => {
                        const usage = navigator.getDiskUsage();
                        const fileCount = navigator.getFileCount();
                        const usedPercent = (usage.used / usage.total) * 100;
                        
                        return (
                          <>
                            <div className="flex justify-between text-xs">
                              <span>Used: {navigator.formatBytes(usage.used)}</span>
                              <span>Free: {navigator.formatBytes(usage.free)}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${usedPercent}%` }}
                              />
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Total: {navigator.formatBytes(usage.total)} • {fileCount.files} files, {fileCount.folders} folders
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* System Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">System Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <MyComputerIcon size={24} className="text-green-600" />
                      <div className="font-medium">MapleOS</div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Version: 26.2</div>
                      <div>Build: 2.8.0</div>
                      <div>Architecture: WASM x64</div>
                    </div>
                  </div>
                  
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <SystemInformationIcon size={24} className="text-orange-600" />
                      <div className="font-medium">System Resources</div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Memory: 8.0 GB</div>
                      <div>Filesystem: Virtual FileSystem</div>
                      <div>Graphics: WebGPU</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Large Icons View */}
              {viewMode === 'large-icons' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => onItemDragStart(e, item)}
                      onDragEnd={onItemDragEnd}
                      onDragOver={item.type === 'folder' ? (e) => onFolderDragOver(e, item.path) : undefined}
                      onDrop={item.type === 'folder' ? (e) => onFolderDrop(e, item.path) : undefined}
                      className={cn(
                        "flex flex-col items-center p-3 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                        selectedItems.includes(item.id) && "bg-primary/20 border border-primary/40",
                        item.type === 'folder' && dragOverTarget === item.path ? "ring-2 ring-primary" : ""
                      )}
                      onClick={(e) => handleItemClick(item.id, e)}
                      onDoubleClick={() => handleDoubleClick(item)}
                      onContextMenu={(e) => handleFileItemRightClick(e, item)}
                    >
                      {renderIconOrThumbnail(item, 64)}
                      {editingItem === item.path ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameSubmit(item);
                            } else if (e.key === 'Escape') {
                              setEditingItem(null);
                              setEditingValue('');
                            }
                          }}
                          className="text-sm text-center mt-2 bg-background border border-border rounded px-1 py-0.5 max-w-full"
                        />
                      ) : (
                        <span className="text-sm text-center mt-2 line-clamp-2">{item.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Medium Icons View */}
              {viewMode === 'medium-icons' && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => onItemDragStart(e, item)}
                      onDragEnd={onItemDragEnd}
                      onDragOver={item.type === 'folder' ? (e) => onFolderDragOver(e, item.path) : undefined}
                      onDrop={item.type === 'folder' ? (e) => onFolderDrop(e, item.path) : undefined}
                      className={cn(
                        "flex flex-col items-center p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                        selectedItems.includes(item.id) && "bg-primary/20 border border-primary/40",
                        item.type === 'folder' && dragOverTarget === item.path ? "ring-2 ring-primary" : ""
                      )}
                      onClick={(e) => handleItemClick(item.id, e)}
                      onDoubleClick={() => handleDoubleClick(item)}
                      onContextMenu={(e) => handleFileItemRightClick(e, item)}
                    >
                      {renderIconOrThumbnail(item, 40)}
                      {editingItem === item.path ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameSubmit(item);
                            } else if (e.key === 'Escape') {
                              setEditingItem(null);
                              setEditingValue('');
                            }
                          }}
                          className="text-xs text-center mt-1 bg-background border border-border rounded px-1 py-0.5 w-full"
                        />
                      ) : (
                        <span className="text-xs text-center mt-1 line-clamp-2">{item.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Small Icons View */}
              {viewMode === 'small-icons' && (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => onItemDragStart(e, item)}
                      onDragEnd={onItemDragEnd}
                      onDragOver={item.type === 'folder' ? (e) => onFolderDragOver(e, item.path) : undefined}
                      onDrop={item.type === 'folder' ? (e) => onFolderDrop(e, item.path) : undefined}
                      className={cn(
                        "flex flex-col items-center p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                        selectedItems.includes(item.id) && "bg-primary/20 border border-primary/40",
                        item.type === 'folder' && dragOverTarget === item.path ? "ring-2 ring-primary" : ""
                      )}
                      onClick={(e) => handleItemClick(item.id, e)}
                      onDoubleClick={() => handleDoubleClick(item)}
                      onContextMenu={(e) => handleFileItemRightClick(e, item)}
                     >
                       {renderIconOrThumbnail(item, 28)}
                       {editingItem === item.path ? (
                         <input
                           ref={editInputRef}
                           type="text"
                           value={editingValue}
                           onChange={(e) => setEditingValue(e.target.value)}
                           onBlur={() => handleRenameSubmit(item)}
                           onKeyDown={(e) => {
                             if (e.key === 'Enter') {
                               handleRenameSubmit(item);
                             } else if (e.key === 'Escape') {
                               setEditingItem(null);
                               setEditingValue('');
                             }
                           }}
                           className="text-xs text-center mt-1 bg-background border border-border rounded px-1 py-0.5 w-full"
                         />
                       ) : (
                         <span className="text-xs text-center mt-1 line-clamp-1">{item.name}</span>
                       )}
                    </div>
                  ))}
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && (
                <div className="grid grid-cols-1 gap-1">
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => onItemDragStart(e, item)}
                      onDragEnd={onItemDragEnd}
                      onDragOver={item.type === 'folder' ? (e) => onFolderDragOver(e, item.path) : undefined}
                      onDrop={item.type === 'folder' ? (e) => onFolderDrop(e, item.path) : undefined}
                      className={cn(
                        "flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors min-h-[48px] md:min-h-0",
                        selectedItems.includes(item.id) && "bg-primary/20 border border-primary/40",
                        item.type === 'folder' && dragOverTarget === item.path ? "ring-2 ring-primary" : ""
                      )}
                      onClick={(e) => handleItemClick(item.id, e)}
                      onDoubleClick={() => handleDoubleClick(item)}
                      onContextMenu={(e) => handleFileItemRightClick(e, item)}
                     >
                       {renderIconOrThumbnail(item, 20)}
                       <div className="flex-1 min-w-0">
                         {editingItem === item.path ? (
                           <input
                             ref={editInputRef}
                             type="text"
                             value={editingValue}
                             onChange={(e) => setEditingValue(e.target.value)}
                             onBlur={() => handleRenameSubmit(item)}
                             onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                 handleRenameSubmit(item);
                               } else if (e.key === 'Escape') {
                                 setEditingItem(null);
                                 setEditingValue('');
                               }
                             }}
                             className="text-sm bg-background border border-border rounded px-2 py-1 flex-1"
                           />
                         ) : (
                           <div className="flex flex-col sm:block">
                             <span className="text-sm md:text-sm font-medium truncate">{item.name}</span>
                             {/* Mobile: Show size and date below name */}
                             <div className="flex text-xs text-muted-foreground gap-3 mt-1 sm:hidden">
                               {item.size && <span>{item.size}</span>}
                               {item.modified && <span>{item.modified}</span>}
                             </div>
                           </div>
                         )}
                       </div>
                      {/* Desktop: Show size and date in columns */}
                      <div className="text-xs text-muted-foreground w-12 md:w-16 hidden sm:block text-right">
                        {item.size || ''}
                      </div>
                      <div className="text-xs text-muted-foreground w-16 md:w-20 hidden md:block text-right">
                        {item.modified}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Details View */}
              {viewMode === 'details' && (
                <div className="space-y-1">
                  {/* Header */}
                  <div className="sticky top-0 z-10 bg-background/95 backdrop-blur 
                  flex items-center gap-2 md:gap-3 p-2 md:p-3 
                  text-xs md:text-sm font-medium text-muted-foreground 
                  border-b border-border">
                    <div className="w-4 md:w-5"></div>
                    <div
                      className="flex items-center gap-1 flex-1 cursor-pointer select-none"
                      onClick={() => handleSort('name')}
                      title="Sort by Name"
                      aria-label="Sort by Name"
                    >
                      Name
                      {sortBy === 'name' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                    <div
                      className="w-12 md:w-16 hidden sm:flex items-center gap-1 justify-end cursor-pointer select-none"
                      onClick={() => handleSort('size')}
                      title="Sort by Size"
                      aria-label="Sort by Size"
                    >
                      Size
                      {sortBy === 'size' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                    <div
                      className="w-32 md:w-40 hidden md:flex items-center gap-1 justify-end cursor-pointer select-none"
                      onClick={() => handleSort('modified')}
                      title="Sort by Modified"
                      aria-label="Sort by Modified"
                    >
                      Modified
                      {sortBy === 'modified' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                  </div>
                  {/* Items */}
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => onItemDragStart(e, item)}
                      onDragEnd={onItemDragEnd}
                      onDragOver={item.type === 'folder' ? (e) => onFolderDragOver(e, item.path) : undefined}
                      onDrop={item.type === 'folder' ? (e) => onFolderDrop(e, item.path) : undefined}
                      className={cn(
                        "flex items-center gap-2 md:gap-3 p-3 md:p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors min-h-[48px] md:min-h-0",
                        selectedItems.includes(item.id) && "bg-primary/20 border border-primary/40",
                        item.type === 'folder' && dragOverTarget === item.path ? "ring-2 ring-primary" : ""
                      )}
                      onClick={(e) => handleItemClick(item.id, e)}
                      onDoubleClick={() => handleDoubleClick(item)}
                      onContextMenu={(e) => handleFileItemRightClick(e, item)}
                     >
                       {renderIconOrThumbnail(item, 20)}
                       <div className="flex-1 min-w-0">
                         {editingItem === item.path ? (
                           <input
                             ref={editInputRef}
                             type="text"
                             value={editingValue}
                             onChange={(e) => setEditingValue(e.target.value)}
                             onBlur={() => handleRenameSubmit(item)}
                             onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                 handleRenameSubmit(item);
                               } else if (e.key === 'Escape') {
                                 setEditingItem(null);
                                 setEditingValue('');
                               }
                             }}
                             className="text-sm md:text-sm bg-background border border-border rounded px-2 py-1 w-full h-8"
                           />
                         ) : (
                           <div className="flex flex-col sm:block">
                             <span className="text-sm md:text-sm font-medium truncate">{item.name}</span>
                             {/* Mobile: Show size and date below name */}
                             <div className="flex text-xs text-muted-foreground gap-3 mt-1 sm:hidden">
                               {item.size && <span>{item.size}</span>}
                               {item.modified && <span>{item.modified}</span>}
                             </div>
                           </div>
                         )}
                       </div>
                      {/* Desktop: Show size and date in columns */}
                      <div className="text-xs text-muted-foreground w-12 md:w-16 hidden sm:block text-right">
                        {item.size || ''}
                      </div>
                      <div className="text-xs text-muted-foreground w-32 md:w-40 hidden md:block text-right">
                        {item.modified}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {currentItems.length === 0 && (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <div className="text-center">
                    <ExplorerFolderIcon size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">This folder is empty</p>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="border-t border-border p-2 bg-muted/50 text-sm text-muted-foreground flex justify-between items-center">
        <div>
          {showMyComputer ? 'My Computer' : (
            selectedItems.length > 0 
              ? `${selectedItems.length} item(s) selected`
              : `${currentItems.length} object(s)`
          )}
        </div>
        <div className="flex items-center gap-4">
          {clipboard && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
              {clipboard.operation === 'copy' ? '📋 Copied' : '✂️ Cut'} {clipboard.items.length} item(s)
            </span>
          )}
          <span className="text-xs">Ready</span>
        </div>
      </div>

      {/* Hidden input for Upload */}
      <input
        ref={uploadInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={(e) => handleUploadFiles(e.target.files)}
      />

      {/* Context Menu */}
      <ContextMenu
        items={contextMenu.items}
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.isVisible}
        onClose={hideContextMenu}
      />
    </div>
  );
};